import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Stack, styled, Tooltip, Grid, Box, IconButton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  getPreDefinedTemplates,
  ITemplateDTO,
  getUserDefinedTemplates,
  favoriteTemplate,
  hideTemplate,
  deleteTemplate,
} from 'wisestep-api';
import {
  WsColors,
  WsDataTable,
  WsButton,
  Item,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  timeSince,
  Spinner,
  CardVariant,
  WsCard,
  IUserDTO,
} from 'wisestep-base';

import { SettingsHeader } from '../../components/SettingsHeader';
import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { useUserContext, useWorkspaceContext } from '../../context';
import { warning, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

import CreateTemplate, { CreateTemplateProps } from './components/templates/CreateTemplate';
import WsSmtpSetup from './WsSmtpSetup';

const StyledStack = styled(Stack)({
  padding: '2',
  backgroundColor: WsColors.TheBlues50,
  gap: 3,
});
const Styles = styled(Stack)({ padding: '0', gap: 0.5 });
const EmailTemplatesList = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [showYes, setShowYes] = useState(false);
  const handleDrawer = () => setShowYes(true);
  const closeDialog = () => setShowYes(false);
  const [bool, setBool] = useState(false);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspaceContext = useWorkspaceContext();
  const navigate = useNavigate();
  const [flagType, setFlagType] = useState<CreateTemplateProps['variant']>('new');
  const [template, setTemplate] = useState<ITemplateDTO[]>([]);
  const [editOrCloneData, setEditOrCloneData] = useState<ITemplateDTO>();
  const [loadPage, setLoadPage] = useState<boolean>(false);
  const [hasData, setHasData] = useState<boolean>(false);

  useEffect(() => {
    (async function () {
      const getPreDefinedTemplatesReq = await getPreDefinedTemplates();
      const getUserDefinedTemplatesReq = await getUserDefinedTemplates();

      const favouredTemplates: ITemplateDTO[] = [];
      const unFavouredTemplates: ITemplateDTO[] = [];

      if (getPreDefinedTemplatesReq.data && getUserDefinedTemplatesReq.data) {
        setHasData(true);
        const totalList = [
          ...getPreDefinedTemplatesReq.data,
          ...getUserDefinedTemplatesReq.data,
        ].map((obj) => ({
          ...obj,
          date: new Date(obj.createdOn as string),
        }));
        const sortedDesc = totalList.sort(
          (objA, objB) => objB.date.getTime() - objA.date.getTime()
        );
        sortedDesc.forEach((template: ITemplateDTO) => {
          if (
            loggedInUser?.email &&
            template.favoredRecruitersByTemplate &&
            template?.favoredRecruitersByTemplate?.indexOf(loggedInUser?.email) > -1
          )
            favouredTemplates.push(template);
          else unFavouredTemplates.push(template);
        });

        setTemplate([...favouredTemplates, ...unFavouredTemplates]);
        setLoadPage(true);
      }
    })();
  }, [loggedInUser?.email]);

  const getLatestData = async () => {
    setHasData(false);
    setLoadPage(false);
    const getPreDefinedTemplatesReq = await getPreDefinedTemplates();
    const getUserDefinedTemplatesReq = await getUserDefinedTemplates();

    const favouredTemplates: ITemplateDTO[] = [];
    const unFavouredTemplates: ITemplateDTO[] = [];

    if (getPreDefinedTemplatesReq.data && getUserDefinedTemplatesReq.data) {
      setHasData(true);
      const totalList = [...getPreDefinedTemplatesReq.data, ...getUserDefinedTemplatesReq.data].map(
        (obj) => ({
          ...obj,
          date: new Date(obj.createdOn as string),
        })
      );
      const sortedDesc = totalList.sort((objA, objB) => objB.date.getTime() - objA.date.getTime());
      sortedDesc.forEach((template: ITemplateDTO) => {
        if (
          loggedInUser?.email &&
          template.favoredRecruitersByTemplate &&
          template?.favoredRecruitersByTemplate?.indexOf(loggedInUser?.email) > -1
        )
          favouredTemplates.push(template);
        else unFavouredTemplates.push(template);
      });

      setTemplate([...favouredTemplates, ...unFavouredTemplates]);
      setLoadPage(true);
    }
  };
  const templateData = useMemo(
    () =>
      template?.map((templatesList) => ({
        title: templatesList.name,
        created_by: templatesList.createdBy,
        created_on: String(timeSince(String(templatesList.createdOn))),
      })),
    [template]
  );
  const clickHandler = async (index: number, ids: number, flag?: string) => {
    switch (ids) {
      case 0:
        try {
          await favoriteTemplate(template[index].id, flag);
          setNotification(success('Successful'));
          getLatestData();
          navigate('/email-settings/templates/');
        } catch (error) {
          setNotification(warning('Something Went Wrong , Please try again'));
        }
        break;
      case 1:
        setFlagType('edit');
        setEditOrCloneData(template[index]);
        handleDrawer();
        break;
      case 2:
        try {
          await hideTemplate(template[index].id, flag);
          setNotification(success('Successful'));
          getLatestData();
          navigate('/email-settings/templates/');
        } catch (error) {
          setNotification(warning('Something Went Wrong , Please try again'));
        }
        break;
      case 3:
        setFlagType('clone');
        setEditOrCloneData(template[index]);
        handleDrawer();

        break;
      case 4:
        try {
          await deleteTemplate(template[index].id);
          setNotification(success('Deleted Successfully'));
          getLatestData();
          navigate('/email-settings/templates/');
        } catch (error) {
          setNotification(warning('Something Went Wrong , Please try again'));
        }
        break;
    }
  };
  const columns = [
    {
      Header: 'Title',
      accessor: 'title',
    },
    {
      Header: 'Created by',
      accessor: 'created_by',
      Cell: (cell: Cell) => {
        return <WsTypography color={WsTypoColors.SENARY}>{cell.value}</WsTypography>;
      },
    },
    {
      Header: 'Created On',
      accessor: 'created_on',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },
    {
      Header: 'Actions',
      id: 'actions',
      Cell: (cell: Cell) => {
        if (
          template[cell.row.index]?.createdBy === (loggedInUser?.email as string) ||
          (loggedInUser?.email as string) === (workspaceContext?.ownerId as string)
        ) {
          setBool(true);
        }
        return (
          <Styles padding={0} direction="row">
            <WsTypography color={WsTypoColors.PRIMARY}>{cell.value}</WsTypography>
            <Item>
              <Tooltip title={'Star'}>
                {template[cell.row.index]?.favoredRecruitersByTemplate?.includes(
                  loggedInUser?.email as string
                ) === true ? (
                  <IconButton onClick={() => clickHandler(cell.row.index, 0, 'false')}>
                    <StarIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => clickHandler(cell.row.index, 0, 'true')}>
                    <StarBorderIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </IconButton>
                )}
              </Tooltip>
            </Item>
            {bool === true && (
              <Item>
                <IconButton onClick={() => clickHandler(cell.row.index, 1)}>
                  <Tooltip title={'Edit'}>
                    <EditIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              </Item>
            )}
            <Item>
              {template[cell.row.index]?.hiddenRecruitersByTemplate?.includes(
                loggedInUser?.email as string
              ) === true ? (
                <IconButton onClick={() => clickHandler(cell.row.index, 2, 'false')}>
                  <Tooltip title={'Show/Hide'}>
                    <VisibilityOffIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              ) : (
                <IconButton onClick={() => clickHandler(cell.row.index, 2, 'true')}>
                  <Tooltip title={'Show/Hide'}>
                    <VisibilityIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              )}
            </Item>
            <Item>
              <IconButton onClick={() => clickHandler(cell.row.index, 3)}>
                <Tooltip title={'Clone'}>
                  <FileCopyIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                </Tooltip>
              </IconButton>
            </Item>
            <Item onClick={() => clickHandler(cell.row.index, 4)}>
              <Tooltip title={'Delete'}>
                <DeleteIcon sx={{ fill: WsColors.Red500, cursor: 'pointer' }} />
              </Tooltip>
            </Item>
          </Styles>
        );
      },
    },
  ];

  return (
    <Box>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['EMAIL_SETTINGS']
        )}
      />
      <SettingsHeader title="Email Settings" />
      <Grid container spacing={1}>
        <Grid item xs={2.5} marginTop={2} pl={0} borderRadius={1.5}>
          <WsSmtpSetup />
        </Grid>
        <Grid marginTop={2} item xs={9.5}>
          {loadPage ? (
            <>
              {hasData ? (
                <>
                  <StyledStack direction={'row'} justifyContent={'space-between'}>
                    <Item>
                      <WsTypography variant={WsTypoVariants.TITLE}>Template List</WsTypography>
                    </Item>
                    <Item>
                      <WsButton
                        onClick={() => {
                          setFlagType('new');
                          handleDrawer();
                        }}
                        variant="contained"
                        size="medium"
                      >
                        Create New Template
                      </WsButton>
                    </Item>
                  </StyledStack>
                  <Stack>
                    <WsDataTable
                      elevated={true}
                      columns={columns}
                      data={templateData}
                      totalDataItems={templateData.length}
                      selectable={false}
                      preSelectedRows={[]}
                      boldItems={['title', 'subject']}
                      pageable={false}
                    />
                    {showYes && (
                      <CreateTemplate
                        openDialog={showYes}
                        closeDialog={closeDialog}
                        onEditOrCloneTemplate={editOrCloneData}
                        variant={flagType}
                        refreshData={getLatestData}
                      ></CreateTemplate>
                    )}
                  </Stack>
                </>
              ) : (
                <Stack>
                  <WsCard
                    cardType={CardVariant.WITH_OUT_BG}
                    title="No Templates Found"
                    description=""
                    middleComponent={<FilePresentOutlinedIcon />}
                    isButton={true}
                    onClick={() => {
                      setFlagType('new');
                      handleDrawer();
                    }}
                    buttonText="Create New Template"
                  />
                </Stack>
              )}
            </>
          ) : (
            <Spinner />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
export default EmailTemplatesList;
